/**
 * RecentChatsView.tsx - Recent Conversations List
 *
 * Displays list of recent chat conversations with hover effects
 */

import { Button, For, Heading, HStack, IconButton, Stack, Text, Box, Spinner, VStack } from "@chakra-ui/react";
import { MenuRoot, MenuTrigger, MenuContent, MenuItem } from "@chakra-ui/react";
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo } from 'react';

import { useChatContext } from '@/contexts/ChatContext';
import { useCircleContext } from '@/contexts/CircleContext';
import { LuPlus, LuTrash2, LuShieldAlert } from 'react-icons/lu';
import { RiMoreLine } from 'react-icons/ri';
import { SmartText } from "@/ui/components/ui/SmartText";
import { useFilterContext } from "@/contexts/FilterContext";
import { useTranslation } from 'react-i18next';
import { brandLogoSrc } from '@/utilities/brandLogo';

const chatVersion = "6.5";

interface RecentChatsViewProps {
    rootDrawer?: any;
    setPostId?: (id: string) => void;
    onDeleteClick: (post: any, onDeleteSuccess: () => void) => void;
}

export function RecentChatsView({ onDeleteClick }: RecentChatsViewProps) {
    const { postId } = useChatContext();
    const { resetFilters } = useFilterContext();
    const { t, i18n } = useTranslation();

    const {
        childCircleId,
        postsState,
        posts: allPosts,
        postsLoaded,
        permissionDenied,
    } = useCircleContext();

    const { deletePost: deletePostFromState } = postsState;

    const navigate = useNavigate();
    const { circleId } = useParams();

    // Filter posts based on the condition - memoized to prevent infinite re-renders
    const filteredPosts = useMemo(() => {
        return (allPosts || []).filter(
            (item: any) => item && item.group?.group?._id === childCircleId && !item.tags?.includes("enhance-temp")
        );
    }, [allPosts, childCircleId]);

    const handleDeleteClick = (post: any, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent chat selection when clicking menu

        // Pass callback to remove from local state after successful deletion
        const onDeleteSuccess = () => {
            deletePostFromState(post);
        };

        onDeleteClick(post, onDeleteSuccess);
    };

    const handleNewChatClick = () => {
        navigate(`/aichat/${circleId}/new`);
        resetFilters();
    };

    const formatDate = (value?: string) => {
        if (!value) return '';
        const date = new Date(value);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString(i18n.language || 'en', { year: 'numeric', month: 'long', day: 'numeric' });
    };
    return (
        <Box className="p-2" height="100%" display="flex" flexDirection="column" background="var(--cair-surface)" border="1px solid var(--cair-divider)">
            {/* Header: "Chats" heading + New Chat button */}
            <Box padding={2} marginBottom={2} width="100%">
                <HStack justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                    <HStack gap={2} flex="1" minWidth={0}>
                        <img src={brandLogoSrc()} alt="" height={20} style={{ height: 20, width: 'auto' }} />
                        <Heading size="md" truncate textAlign={i18n.dir() === 'rtl' ? 'right' : 'left'}>{t("chats")}</Heading>
                    </HStack>
                    <Button
                        size="xs"
                        background="var(--cair-accent)"
                        color="white"
                        borderRadius="var(--cair-radius-lg)"
                        _hover={{ background: "var(--cair-accent-hover)" }}
                        flexShrink={0}
                        onClick={handleNewChatClick}
                    >
                        <LuPlus />
                        {t("new_chat")}
                    </Button>
                </HStack>
            </Box>

            {/* Chat List */}
            <Stack gap={0} flex="1" overflowY="auto">
                {/* Permission Denied State */}
                {permissionDenied && (
                    <VStack paddingY={8} gap={3}>
                        <Box as={LuShieldAlert} fontSize="2xl" color="red.500" />
                        <Text fontSize="sm" color="red.600" fontWeight="bold">
                            {t("permission_denied_title")}
                        </Text>
                    </VStack>
                )}

                {/* Loading State - only show if NOT permission denied */}
                {!postsLoaded && !permissionDenied && (
                    <VStack paddingY={8} gap={3}>
                        <Spinner size="lg" color="#333333" />
                        <Text fontSize="sm" color="#333333">{t("loading")}</Text>
                    </VStack>
                )}

                {/* Posts List */}
                {postsLoaded && (
                    <For each={filteredPosts}>
                        {(item, index) => (
                        <Box
                            key={item._id}
                            position="relative"
                            paddingX={4}
                            paddingY={3}
                            borderRadius="0"
                            borderTop={index === 0 ? "1px solid var(--cair-border)" : "none"}
                            borderBottom="1px solid var(--cair-border)"
                            cursor="pointer"
                            background={postId === item._id ? "var(--cair-surface-elevated)" : "transparent"}
                            transition="all 0.2s ease-in-out"
                            onClick={() => navigate(`/aichat/${circleId}/chat/${item._id}`)}
                            css={{
                                '&:hover': {
                                    backgroundColor: 'var(--cair-surface-elevated)'
                                },
                                '&:hover .menu-button': {
                                    opacity: 1
                                }
                            }}
                        >
                            <HStack justifyContent="space-between" gap={2}>
                                <SmartText
                                    text={item.title}
                                    dir="auto"
                                    fontWeight={postId === item._id ? "bold" : "normal"}
                                    fontSize="sm"
                                    textOverflow="ellipsis"
                                    overflow="hidden"
                                    whiteSpace="nowrap"
                                    textAlign={i18n.dir() === 'rtl' ? 'right' : 'left'}
                                    flex="1"
                                >
                                    {item.title}
                                </SmartText>

                                <MenuRoot positioning={{ placement: "bottom-end" }}>
                                    <MenuTrigger asChild>
                                        <IconButton
                                            className="menu-button"
                                            aria-label={t("more_options") || "More options"}
                                            variant="ghost"
                                            size="md"
                                            onClick={(e) => e.stopPropagation()}
                                            css={{
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                width: '20px',
                                                height: '20px',
                                            }}
                                        >
                                            <RiMoreLine />
                                        </IconButton>
                                    </MenuTrigger>
                                    <MenuContent
                                        minWidth="140px"
                                        backgroundColor="white"
                                        borderRadius="6px"
                                        boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
                                        padding="4px"
                                        position="absolute"
                                        right={i18n.dir() === 'rtl' ? 'auto' : 0}
                                        left={i18n.dir() === 'rtl' ? 0 : 'auto'}
                                        top="35px"
                                        zIndex={1000}
                                    >
                                        <MenuItem
                                            value="delete"
                                            onClick={(e) => handleDeleteClick(item, e as any)}
                                            padding="8px 12px"
                                            borderRadius="4px"
                                            cursor="pointer"

                                        >
                                            {i18n.dir() === 'rtl' ? (
                                                <HStack textAlign="right" width="100%" justifyContent="flex-end">
                                                    <Text
                                                        color="red.600"
                                                        fontSize="14px"
                                                        fontWeight="500"
                                                    >
                                                        {t("delete")}
                                                    </Text>
                                                    <Box
                                                        as={LuTrash2}
                                                        color="red.600"
                                                        fontSize="16px"
                                                        flexShrink={0}
                                                    />
                                                </HStack>
                                            ) : (
                                                <HStack>
                                                    <Box
                                                        as={LuTrash2}
                                                        color="red.600"
                                                        fontSize="16px"
                                                        flexShrink={0}
                                                    />
                                                    <Text
                                                        color="red.600"
                                                        fontSize="14px"
                                                        fontWeight="500"
                                                    >
                                                        {t("delete")}
                                                    </Text>
                                                </HStack>
                                            )}
                                        </MenuItem>
                                    </MenuContent>
                                </MenuRoot>
                            </HStack>
                            {formatDate(item.created) && (
                                <Text
                                    fontSize="xs"
                                    color="var(--cair-text-secondary)"
                                    marginTop={1}
                                    textAlign={i18n.dir() === 'rtl' ? 'right' : 'left'}
                                >
                                    {formatDate(item.created)}
                                </Text>
                            )}
                        </Box>
                        )}
                    </For>
                )}

                {/* Empty State - Only show when loaded and no posts */}
                {postsLoaded && filteredPosts.length === 0 && (
                    <Box paddingX={3} paddingY={4}>
                        <SmartText dir="auto" color="fg.muted" fontSize="sm" textAlign="center">
                            {t("no_past_chats")}
                        </SmartText>
                    </Box>
                )}
            </Stack>
            <Box paddingX={3} paddingY={4}>
                <Text fontSize="sm" className="hidden-version-text" color="transparent">
                    {t("version")} {chatVersion}
                </Text>
            </Box>
        </Box>
    )
}
